import { motion } from "framer-motion";

const easeOut = [0.16, 1, 0.3, 1];

// Line-by-line masked reveal for editorial headings.
// Pass `lines` (array of strings) OR children (single string).
export const MaskReveal = ({ lines, children, delay = 0, className = "", el = "h1" }) => {
  const items = lines || (typeof children === "string" ? children.split("\n") : [children]);
  const Tag = motion[el] || motion.h1;
  return (
    <Tag className={className} initial="hidden" animate="show" viewport={{ once: true }}>
      {items.map((line, i) => (
        <span
          key={i}
          style={{ display: "block", overflow: "hidden", paddingBottom: "0.05em" }}
        >
          <motion.span
            style={{ display: "inline-block", willChange: "transform" }}
            variants={{
              hidden: { y: "110%" },
              show: {
                y: "0%",
                transition: { duration: 1.0, ease: easeOut, delay: delay + i * 0.12 },
              },
            }}
            initial="hidden"
            animate="show"
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

/**
 * Reveal on scroll — or on mount, for anything already on screen.
 *
 * whileInView with a -80px margin makes the trigger zone on a 900px-tall
 * window y 80..820. A hero button sitting at y 835 is inside the window and
 * outside that zone, so it stayed at opacity 0 until the visitor scrolled:
 * measured on 1440x900, "Book a Consultation" was invisible on arrival.
 * Anything that can be above the fold on load passes onMount instead.
 */
export const FadeUp = ({ children, delay = 0, y = 30, className = "", once = true, onMount = false }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    {...(onMount
      ? { animate: { opacity: 1, y: 0 } }
      : { whileInView: { opacity: 1, y: 0 }, viewport: { once, margin: "-80px" } })}
    transition={{ duration: 0.9, ease: easeOut, delay }}
  >
    {children}
  </motion.div>
);

export const StaggerChildren = ({ children, className = "", stagger = 0.08 }) => (
  <motion.div
    className={className}
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-80px" }}
    variants={{
      hidden: { opacity: 0 },
      show: { opacity: 1, transition: { staggerChildren: stagger } },
    }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children, className = "", y = 24 }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y },
      show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: easeOut } },
    }}
  >
    {children}
  </motion.div>
);
